import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, ArrowLeft, Eye, Users, Globe2, MapPin,
  ExternalLink, FileText, Monitor, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import Logo from '@/components/site/Logo';
import { useAuth } from '@/contexts/AuthContext';

const RANGES = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-surface border border-brandborder p-6">
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-gold" />
      <div className="overline">{label}</div>
    </div>
    <div className="mt-3 font-display text-3xl lg:text-4xl text-maroon">{value}</div>
    {hint != null && <div className="mt-1 text-xs text-charcoal/55">{hint}</div>}
  </div>
);

const RankBar = ({ label, value, max, sub }) => (
  <div className="py-3 border-b border-brandborder/60">
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <div className="truncate text-charcoal">{label}</div>
      <div className="text-charcoal/70 shrink-0">{value}</div>
    </div>
    {sub && <div className="text-[11px] text-charcoal/50 mt-0.5 truncate">{sub}</div>}
    <div className="mt-2 h-1 bg-brandborder/60 relative">
      <div className="absolute inset-y-0 left-0 bg-maroon" style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
    </div>
  </div>
);

// Simple SVG bar chart for daily views
const DailyChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.views));
  const barW = 100 / Math.max(1, data.length);
  return (
    <div className="w-full h-56 relative">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {data.map((d, i) => {
          const h = (d.views / max) * 90;
          return (
            <g key={d.day}>
              <rect x={i * barW + barW * 0.15} y={100 - h} width={barW * 0.7} height={h} className="fill-maroon" />
            </g>
          );
        })}
      </svg>
      <div className="flex text-[10px] uppercase tracking-[0.15em] text-charcoal/55 mt-1">
        {data.map((d) => (
          <div key={d.day} className="flex-1 text-center truncate">
            {d.day?.slice(5)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminTraffic() {
  const { user } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/traffic', { params: { days: d } });
      setData(res.data);
    } catch (e) { toast.error(formatApiError(e, 'Could not load traffic')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(days); }, [days]);

  const topPageMax = useMemo(() => Math.max(1, ...(data?.top_pages || []).map((p) => p.views)), [data]);
  const topRefMax = useMemo(() => Math.max(1, ...(data?.top_referrers || []).map((p) => p.views)), [data]);
  const topCountryMax = useMemo(() => Math.max(1, ...(data?.top_countries || []).map((p) => p.views)), [data]);
  const topCityMax = useMemo(() => Math.max(1, ...(data?.top_cities || []).map((p) => p.views)), [data]);
  const deviceMax = useMemo(() => Math.max(1, ...(data?.device_breakdown || []).map((p) => p.views)), [data]);

  return (
    <main className="min-h-screen bg-ivory text-charcoal" data-testid="admin-traffic">
      <header className="border-b border-brandborder bg-surface sticky top-0 z-30">
        <div className="container-luxe flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <div className="overline">Trendy Closet</div>
              <div className="font-display text-lg leading-tight">Traffic & Visitors</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
            <span className="hidden md:inline text-sm text-charcoal/60">{user?.email}</span>
          </div>
        </div>
      </header>

      <section className="container-luxe py-10 lg:py-14">
        {/* Range selector */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <span className="overline">Analytics</span>
            <h1 className="font-display text-3xl lg:text-4xl mt-2">Who's browsing your closet.</h1>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-charcoal/55" data-testid="tz-label">
              All times shown in Eastern Time (ET)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  days === r.days ? 'bg-burgundy text-ivory border-burgundy' : 'border-brandborder text-charcoal/70 hover:border-maroon hover:text-maroon'
                }`}
              >{r.label}</button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat icon={Eye} label="Today · views" value={data.today_views} hint={`Yesterday: ${data.yesterday_views}`} />
              <Stat icon={Users} label="Today · visitors" value={data.today_uniques} hint="unique IPs" />
              <Stat icon={TrendingUp} label={`${days}d views`} value={data.total_views} />
              <Stat icon={Users} label={`${days}d visitors`} value={data.total_uniques} hint="unique IPs" />
            </div>

            {/* Daily chart */}
            <div className="bg-surface border border-brandborder p-6 mb-10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="overline">Daily views</span>
                  <div className="font-display text-xl">Last {days} day{days > 1 ? 's' : ''}</div>
                </div>
                <div className="text-xs text-charcoal/55">Bars = pageviews per day</div>
              </div>
              <DailyChart data={data.daily} />
            </div>

            {/* Two-column stats */}
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-surface border border-brandborder p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-4 h-4 text-gold" />
                  <span className="overline">Top pages</span>
                </div>
                {data.top_pages.length === 0 ? (
                  <p className="text-sm text-charcoal/55">No pageviews yet.</p>
                ) : data.top_pages.map((p) => (
                  <RankBar key={p.path} label={p.path} value={p.views} max={topPageMax} />
                ))}
              </div>

              <div className="bg-surface border border-brandborder p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ExternalLink className="w-4 h-4 text-gold" />
                  <span className="overline">Top referrers</span>
                </div>
                {data.top_referrers.length === 0 ? (
                  <p className="text-sm text-charcoal/55">No external referrers yet.</p>
                ) : data.top_referrers.map((p) => (
                  <RankBar key={p.referrer} label={p.referrer} value={p.views} max={topRefMax} />
                ))}
              </div>

              <div className="bg-surface border border-brandborder p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe2 className="w-4 h-4 text-gold" />
                  <span className="overline">Top countries</span>
                </div>
                {data.top_countries.length === 0 ? (
                  <p className="text-sm text-charcoal/55">No geolocated visitors yet.</p>
                ) : data.top_countries.map((p) => (
                  <RankBar key={p.country} label={`${p.code ? p.code + ' · ' : ''}${p.country}`} value={p.views} sub={`${p.uniques} unique`} max={topCountryMax} />
                ))}
              </div>

              <div className="bg-surface border border-brandborder p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gold" />
                  <span className="overline">Top cities</span>
                </div>
                {data.top_cities.length === 0 ? (
                  <p className="text-sm text-charcoal/55">No cities recorded yet.</p>
                ) : data.top_cities.map((p) => (
                  <RankBar key={`${p.city}-${p.country}`} label={p.city} sub={p.country} value={p.views} max={topCityMax} />
                ))}
              </div>

              <div className="bg-surface border border-brandborder p-6 lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="w-4 h-4 text-gold" />
                  <span className="overline">Device breakdown</span>
                </div>
                {data.device_breakdown.length === 0 ? (
                  <p className="text-sm text-charcoal/55">No devices tracked yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-6">
                    {data.device_breakdown.map((d) => (
                      <div key={d.device}>
                        <div className="overline">{d.device}</div>
                        <div className="font-display text-3xl text-maroon mt-1">{d.views}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </main>
  );
}
